<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeCheckbox from "@dsk/components/Checkbox/LeCheckbox.vue";
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeModal from "@dsk/components/LeModal.vue";
import type { ModalProps } from "@dsk/types";
import { storeToRefs } from "pinia";
import { useField, useForm } from "vee-validate";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { ValidationHelper } from "@/helpers";
import { ArticleFeedbackService } from "@/services";
import { useSearchStore, useUserStore } from "@/stores";
import type { DictsysArticle } from "@/types";
import type { LeArticleFeedbackModalProps } from "@/types/components/le-article-feedback-modal.type";
import { articleFeedbackFormValidator } from "@/validation";

const props = defineProps<ModalProps & LeArticleFeedbackModalProps>();

const emit = defineEmits<{
  close: [didSubmit: boolean];
}>();

const { t } = useI18n();
const router = useRouter();

const { query } = storeToRefs(useSearchStore());
const { userData } = storeToRefs(useUserStore());

const usernameIsEmail = userData.value?.username.includes("@");

const articleIsDictsysArticle = (article: string | DictsysArticle): article is DictsysArticle =>
  typeof article !== "string";

const { handleSubmit, errors, resetForm } = useForm({
  initialValues: {
    name: "",
    email: usernameIsEmail ? userData.value?.username : "",
    feedback: "",
    canContact: false,

    dictionaryId: "",
    dictionaryTitle: "",
    methodId: "",
    url: "",

    query: null,
    lemma: null,
    wordclass: null
  },
  validationSchema: articleFeedbackFormValidator
});

const { value: name, meta: nameMeta } = useField<string>("name");
const { value: email, meta: emailMeta } = useField<string>("email");
const { value: feedback, meta: feedbackMeta } = useField<string>("feedback");
const { value: canContact } = useField<boolean>("canContact");

const hasApiError = ref<boolean>(false);

const onSubmit = handleSubmit(async (form) => {
  hasApiError.value = false;

  form.dictionaryId = props.dictionary?.id ?? "";
  form.dictionaryTitle = props.dictionary?.title ?? "";
  form.methodId = props.methodId;
  form.url = window.location.href;

  form.query = query.value;
  form.lemma = articleIsDictsysArticle(props.article) ? props.article.article.headWord : null;
  form.wordclass = articleIsDictsysArticle(props.article) ? props.article.article.wordclass.danishShort : null;

  try {
    await ArticleFeedbackService.sendArticleFeedback(form);
  } catch (e) {
    hasApiError.value = true;
  }

  onClose(true);
});

const onClose = (didSubmit: boolean) => {
  resetForm();
  emit("close", didSubmit);
};
</script>

<template>
  <LeModal class="le-article-feedback-modal" :is-open="props.isOpen" @close="onClose(false)">
    <template #title>
      <LeHeading tag="h1" size="m">{{ t("le-dictionary-feedback.modal.title.top") }}</LeHeading>
    </template>

    <template #body>
      <form id="le-article-feedback-modal-form" @submit.prevent="onSubmit()">
        <p>{{ t("le-dictionary-feedback.modal.text") }}</p>

        <fieldset class="le-article-feedback-modal__section le-article-feedback-modal__section--user-info">
          <LeHeading tag="h2" size="s">
            {{ t("le-dictionary-feedback.modal.title.about") }}
          </LeHeading>

          <div>
            <label for="le-article-feedback-modal-name" class="le-article-feedback-modal__field-label">
              {{ t("le-dictionary-feedback.modal.form.username.label") }}
            </label>
            <LeInput
              id="le-article-feedback-modal-name"
              v-model="name"
              type="text"
              :placeholder="t('le-dictionary-feedback.modal.form.username.placeholder')"
              :has-error="ValidationHelper.displayError(nameMeta)"
            />
            <small
              v-if="errors.name"
              class="le-article-feedback-modal__field-error le-article-feedback-modal__field-error--small"
            >
              {{ errors.name }}
            </small>
          </div>

          <div>
            <label for="le-article-feedback-modal-email" class="le-article-feedback-modal__field-label">
              {{ t("le-dictionary-feedback.modal.form.email.label") }}
            </label>
            <LeInput
              id="le-article-feedback-modal-email"
              v-model="email"
              type="text"
              :placeholder="t('le-dictionary-feedback.modal.form.email.placeholder')"
              :has-error="ValidationHelper.displayError(emailMeta)"
            />
            <small
              v-if="ValidationHelper.displayError(emailMeta) && errors.email"
              class="le-article-feedback-modal__field-error le-article-feedback-modal__field-errors--small"
            >
              {{ errors.email }}
            </small>
          </div>
        </fieldset>

        <section class="le-article-feedback-modal__section le-article-feedback-modal__section--article-feedback">
          <LeHeading tag="h2" size="s">
            {{ t("le-dictionary-feedback.modal.title.feedback") }}
          </LeHeading>

          <div v-if="props.dictionary">
            <div v-if="articleIsDictsysArticle(props.article)">
              <span class="le-article-feedback-modal__headword">{{ props.article.article.headWord }}</span>
              <span class="le-article-feedback-modal__wordclass">
                &ThinSpace;{{ props.article.article.wordclass.danishShort }}
              </span>
            </div>
            <i18n-t tag="div" keypath="le-dictionary-feedback.modal.dictionary-publisher.text">
              <template #dictionary>
                <span class="le-article-feedback-modal__dictionary-title">{{ props.dictionary.title }}</span>
              </template>
              <template #publisher>
                {{ props.dictionary.publisher }}
              </template>
            </i18n-t>
          </div>

          <div>
            <label for="le-article-feedback-modal-feedback" class="le-article-feedback-modal__field-label">
              {{ t("le-dictionary-feedback.modal.form.feedback.label") }}
            </label>
            <textarea
              id="le-article-feedback-modal-feedback"
              v-model="feedback"
              :class="[
                'le-article-feedback-modal__feedback',
                { 'le-article-feedback-modal__feedback--error': ValidationHelper.displayError(feedbackMeta) }
              ]"
              :placeholder="t('le-dictionary-feedback.modal.form.feedback.placeholder')"
              :has-error="ValidationHelper.displayError(feedbackMeta)"
            />
            <small
              v-if="errors.feedback"
              class="le-article-feedback-modal__field-error le-article-feedback-modal__field-error--small"
            >
              {{ errors.feedback }}
            </small>
          </div>
        </section>

        <section class="le-article-feedback-modal__section le-article-feedback-modal__section--consent">
          <label>
            <LeCheckbox v-model="canContact" />
            <span :class="{ 'le-article-feedback-modal__field-error': errors.canContact }">
              {{ t("le-dictionary-feedback.modal.accept-contact.text") }}
            </span>
          </label>
          <i18n-t tag="span" keypath="le-dictionary-feedback.modal.privacypolicy.text">
            <template #privacyPolicyLink>
              <a :href="router.resolve({ name: 'policies' }).href" target="_blank">
                {{ t("le-dictionary-feedback.modal.privacypolicy.link-text") }}
              </a>
            </template>
          </i18n-t>
        </section>
      </form>
    </template>

    <template #buttons>
      <LeButton variant="outline" @click="onClose(false)">
        {{ t("le-dictionary-feedback.modal.cancel.button") }}
      </LeButton>
      <LeButton type="submit" form="le-article-feedback-modal-form">
        {{ t("le-dictionary-feedback.modal.send-feedback.button") }}
      </LeButton>
    </template>
  </LeModal>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-article-feedback-modal {
  &__section {
    display: flex;
    flex-direction: column;
    margin-top: 20px;
    padding: 0;
    gap: 12px;

    &--user-info {
      margin-top: 30px;
    }

    &--article-feedback {
      margin-bottom: 30px;
    }

    &--consent {
      label {
        display: flex;
        align-items: start;
        gap: 8px;
      }

      a {
        .link();
        color: @colors[@neutrals][500];
      }
    }
  }

  &__field-label {
    .body-text("xs");
    color: @colors[@neutrals][200];
  }

  &__field-error {
    color: @colors[@error][600];

    &--small {
      .body-text("xs");
      color: @colors[@error][600];
    }
  }

  &__headword {
    .body-text("m", "bold");
    color: @colors[@primary][700];
  }

  &__dictionary-title {
    .body-text("m", "bold");
    color: @colors[@neutrals][300];
  }

  &__feedback {
    display: block;
    width: 100%;
    min-height: 139px;
    padding: 8px 12px;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    background: @colors[@neutrals][100];
    resize: vertical;

    &::placeholder {
      opacity: 0.4;
      color: @colors[@neutrals][500];
    }

    &:focus-within {
      .focus-outline(green);
    }

    &--error {
      border-color: @colors[@error][600];

      &:focus-within {
        .focus-outline(red);
      }
    }
  }
}
</style>
